import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { RotateCcw, Printer, LogOut } from "lucide-react";
import HppyfxMark from "@/components/HppyfxMark";
import { toast } from "sonner";

import ExecutiveSnapshot from "@/components/tabs/ExecutiveSnapshot";
import RevenueIntelligence from "@/components/tabs/RevenueIntelligence";
import CostResourcePressure from "@/components/tabs/CostResourcePressure";
import Forecasting from "@/components/tabs/Forecasting";
import EconomistInsights from "@/components/tabs/EconomistInsights";

import { fmtCurrency, fmtNumber, fmtPct } from "@/lib/format";
import { fetchWhatIf } from "@/lib/api";

const TABS = [
  ["snapshot", "Executive Snapshot"],
  ["revenue", "Revenue Intelligence"],
  ["cost", "Cost & Resource Pressure"],
  ["forecast", "Forecasting"],
  ["insights", "AI Economist Insights"],
];

export default function Dashboard({ data, user, onReset, onSignOut }) {
  const [tab, setTab] = React.useState("snapshot");
  const [forecast, setForecast] = React.useState(data.forecast);
  const [scenarios, setScenarios] = React.useState([]);

  React.useEffect(() => {
    setForecast(data.forecast);
  }, [data.forecast]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchWhatIf(data.session_id);
        setScenarios(res.scenarios || []);
      } catch {
        setScenarios([]);
      }
    })();
  }, [data.session_id]);

  const analysis = data.analysis;
  const k = analysis.kpis || {};
  const totalRevenue = k.total_revenue;

  const handleExport = (kind) => {
    if (kind === "pdf") {
      window.print();
      return;
    }
    // plain text summary
    const lines = [
      `HppyFx Executive Summary — ${data.filename}`,
      `Generated ${new Date().toLocaleString()}`,
      "",
      "KEY METRICS",
      `- Total Revenue: ${fmtCurrency(k.total_revenue)}`,
      `- Net Collected: ${fmtCurrency(k.net_collected_revenue)}`,
      `- Total Claims: ${fmtNumber(k.total_claims)}`,
      `- Denial Rate: ${k.denial_rate_pct !== null ? fmtPct(k.denial_rate_pct) : "—"}`,
      `- Avg Margin: ${k.avg_margin_pct !== null ? fmtPct(k.avg_margin_pct) : "—"}`,
      `- Avg LOS: ${k.avg_length_of_stay !== null ? k.avg_length_of_stay + " days" : "—"}`,
      `- Bed Occupancy: ${k.avg_bed_occupancy_pct !== null ? fmtPct(k.avg_bed_occupancy_pct) : "—"}`,
      "",
      "TOP REVENUE OPPORTUNITIES",
      ...(analysis.top_revenue_opportunities || []).map(
        (o, i) => `${i + 1}. ${o.title} — ${fmtCurrency(o.estimate)} (${o.basis})`
      ),
      "",
      "AI ECONOMIST INSIGHTS",
      ...(analysis.insights || []).flatMap((ins, i) => [
        `${i + 1}. [${ins.tag}] ${ins.title}`,
        `   What: ${ins.what}`,
        `   Why: ${ins.why}`,
        `   Action: ${ins.action}`,
        "",
      ]),
      "",
      "WHAT-IF SCENARIOS",
      ...scenarios.map(
        (s, i) =>
          `${i + 1}. ${s.title} — Revenue +${fmtCurrency(s.revenue_gain)}, Cost save ${fmtCurrency(
            s.cost_saving
          )}, Care risk: ${s.care_risk}`
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `HppyFx-summary-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Executive summary downloaded");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-screen">
      <header className="border-b border-gray-200 sticky top-0 bg-white z-20 no-print">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <HppyfxMark variant="default" className="h-8 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1 truncate">
                {data.filename} · {fmtNumber(analysis.rows)} rows ·{" "}
                {Object.keys(analysis.detected).length} fields
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:block text-right mr-2">
              <div className="text-xs text-gray-500">Signed in as</div>
              <div className="text-sm font-semibold text-gray-900">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("pdf")}
              className="rounded-none h-9 border-gray-300 hover:bg-gray-50"
              data-testid="header-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Print / PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              className="rounded-none h-9 border-gray-300 hover:bg-gray-50"
              data-testid="reset-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} />
              New dataset
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSignOut}
              className="rounded-none h-9 border-gray-300 hover:bg-gray-50"
              data-testid="dashboard-signout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8 md:py-10">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="border-b border-gray-200 mb-8 no-print overflow-x-auto">
            <TabsList className="bg-transparent h-auto p-0 rounded-none gap-0 w-auto justify-start">
              {TABS.map(([v, l]) => (
                <TabsTrigger
                  key={v}
                  value={v}
                  className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#5E3BFF] data-[state=active]:bg-transparent data-[state=active]:text-gray-950 data-[state=active]:font-bold data-[state=active]:shadow-none px-5 py-3 text-sm text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap"
                  data-testid={`tab-${v}`}
                >
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="snapshot" className="mt-0">
            <ExecutiveSnapshot analysis={analysis} forecast={forecast} />
          </TabsContent>
          <TabsContent value="revenue" className="mt-0">
            <RevenueIntelligence analysis={analysis} />
          </TabsContent>
          <TabsContent value="cost" className="mt-0">
            <CostResourcePressure analysis={analysis} />
          </TabsContent>
          <TabsContent value="forecast" className="mt-0">
            <Forecasting
              sessionId={data.session_id}
              forecast={forecast}
              setForecast={setForecast}
            />
          </TabsContent>
          <TabsContent value="insights" className="mt-0">
            <EconomistInsights
              analysis={analysis}
              scenarios={scenarios}
              totalRevenue={totalRevenue}
              onExport={handleExport}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
